import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Grid, GridColumn, GridItemChangeEvent, GridCellProps, GridSortChangeEvent } from "@progress/kendo-react-grid";
import { Button } from '@progress/kendo-react-buttons';
import { plusIcon, trashIcon } from '@progress/kendo-svg-icons';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { Endpoints, EndpointStatus, Groups } from '../../../../Models/Assets/AssetTemplateReadDto';
import { enMessages } from '../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';

function isEndpointActive(ep: Endpoints): boolean {
  if (ep.status !== undefined) return ep.status === 'Active';
  return ep.isEnabled !== false;
}

function isEndpointNameDuplicate(
  newName: string,
  endpoints: Endpoints[],
  excludeEndpoint: Endpoints
): boolean {
  const normalized = (newName || '').trim().toLowerCase();
  if (!normalized) return false;
  const isSameEndpoint = (ep: Endpoints) =>
    ep.id === excludeEndpoint.id &&
    ep.groupId === excludeEndpoint.groupId &&
    (excludeEndpoint.pointId == null || ep.pointId === excludeEndpoint.pointId);
  return endpoints.some(
    (ep) =>
      isEndpointActive(ep) &&
      !isSameEndpoint(ep) &&
      ep.name?.trim().toLowerCase() === normalized
  );
}

interface Props {
  endpoints?: Endpoints[];
  assetData?: any;
  selectedGroupId?: string | null;
  onEndpointsChange?: (endpoints: Endpoints[]) => void;
  onEditStateChange?: (isEditing: boolean) => void;
  onAddEndpoint?: () => void;
  canAddEndpoint?: boolean;
  instructionText?: string;
  isEditTemplate?: boolean;
}

const DATA_ITEM_KEY = 'id';
const INITIAL_SORT: SortDescriptor[] = [{ field: 'name', dir: 'asc' }];

function getEndpointRowKey(endpoint: Endpoints): string {
  return `${endpoint.groupId}:${endpoint.id}:${endpoint.pointId ?? 'none'}`;
}

const EndpointsTableTemplateComponent: React.FC<Props> = ({
  assetData = {},
  selectedGroupId,
  onEndpointsChange,
  endpoints = [],
  onAddEndpoint,
  canAddEndpoint = true,
  instructionText,
  isEditTemplate = false,
}) => {
  const localizationService = useLocalization();
  const [nameError, setNameError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortDescriptor[]>(INITIAL_SORT);
  const [orderedRowKeys, setOrderedRowKeys] = useState<string[]>([]);
  const previousVisibleKeysSignatureRef = useRef<string>('');
  const previousSortSignatureRef = useRef<string>('');

  const visibleEndpoints = useMemo(() => {
    const byGroup = selectedGroupId === '0'
      ? endpoints
      : selectedGroupId === null
        ? []
        : endpoints.filter((ep: Endpoints) => ep.groupId === selectedGroupId);
    return byGroup.filter(isEndpointActive);
  }, [endpoints, selectedGroupId]);

  const visibleEndpointKeysSignature = useMemo(
    () => visibleEndpoints.map(getEndpointRowKey).join('|'),
    [visibleEndpoints]
  );

  useEffect(() => {
    const sortSignature = JSON.stringify(sort);
    const didSortChange = previousSortSignatureRef.current !== sortSignature;
    const didVisibleRowsChange = previousVisibleKeysSignatureRef.current !== visibleEndpointKeysSignature;

    if (!didSortChange && !didVisibleRowsChange) {
      return;
    }

    const sortedEndpoints = sort.length > 0 ? orderBy(visibleEndpoints, sort) : visibleEndpoints;
    setOrderedRowKeys(sortedEndpoints.map(getEndpointRowKey));
    previousSortSignatureRef.current = sortSignature;
    previousVisibleKeysSignatureRef.current = visibleEndpointKeysSignature;
  }, [sort, visibleEndpointKeysSignature, visibleEndpoints]);

  const filteredEndpoints = useMemo(() => {
    const endpointsByKey = new Map(
      visibleEndpoints.map((endpoint) => [getEndpointRowKey(endpoint), endpoint])
    );

    const orderedEndpoints = orderedRowKeys.length > 0
      ? orderedRowKeys
        .map((rowKey) => endpointsByKey.get(rowKey))
        .filter((endpoint): endpoint is Endpoints => endpoint !== undefined)
      : (sort.length > 0 ? orderBy(visibleEndpoints, sort) : visibleEndpoints);

    return orderedEndpoints.map((ep) => ({ ...ep, inEdit: true }));
  }, [orderedRowKeys, sort, visibleEndpoints]);

  const selectedGroupName = assetData.groups?.find((group: Groups) => group.id === selectedGroupId)?.name || 'All Groups';

  const handleItemChange = (event: GridItemChangeEvent) => {
    const dataItem = event.dataItem as Endpoints;

    if (selectedGroupId && dataItem.groupId !== selectedGroupId) {
      return;
    }

    const newEndpoints = endpoints.map((endpoint: Endpoints) => {
      const isMatch =
        endpoint.id === dataItem.id &&
        endpoint.groupId === dataItem.groupId &&
        (dataItem.pointId != null ? endpoint.pointId === dataItem.pointId : true);
      return isMatch ? { ...endpoint, [event.field!]: event.value } : endpoint;
    });
    onEndpointsChange?.(newEndpoints);

    if (event.field === 'name') {
      const newName = (event.value as string) ?? '';
      if (!newName.trim()) {
        setNameError(localizationService.toLanguageString('custom.uniqueName', enMessages.custom.uniqueName));
      } else if (isEndpointNameDuplicate(newName, newEndpoints, dataItem)) {
        setNameError(localizationService.toLanguageString('custom.endpointNameExistsInTemplate', 'An endpoint with this name already exists in this template. Names must be unique.'));
      } else {
        setNameError(null);
      }
    }
  };

  const handleDeleteEndpoint = (dataItem: Endpoints) => {
    if (selectedGroupId && dataItem.groupId !== selectedGroupId) {
      return;
    }
    const index = endpoints.findIndex(
      (ep) =>
        ep.id === dataItem.id &&
        ep.groupId === dataItem.groupId &&
        (dataItem.pointId != null ? ep.pointId === dataItem.pointId : true)
    );
    if (index === -1) return;
    if (isEditTemplate) {
      const updatedEndpoints = endpoints.map((ep, i) =>
        i === index ? { ...ep, status: 'Deleted' as EndpointStatus } : ep
      );
      onEndpointsChange?.(updatedEndpoints);
    } else {
      const updatedEndpoints = [...endpoints.slice(0, index), ...endpoints.slice(index + 1)];
      onEndpointsChange?.(updatedEndpoints);
    }
  };

  const deleteCell = (props: GridCellProps) => {
    const { dataItem } = props;
    return (
      <td style={{ textAlign: 'center' }}>
        <Button
          type="button"
          themeColor="primary"
          svgIcon={trashIcon}
          title={localizationService.toLanguageString('custom.delete', enMessages.custom.delete)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDeleteEndpoint(dataItem);
          }}
        />
      </td>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between align-middle mb-2">
        <div className="text-xl font-bold">
          {localizationService.toLanguageString('custom.endpoints', enMessages.custom.endpoints)} ({selectedGroupName})
        </div>
        <div className="flex items-center gap-2">
          {onAddEndpoint && (
            <Button
              type="button"
              themeColor="primary"
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              svgIcon={plusIcon}
              disabled={!canAddEndpoint}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (canAddEndpoint) onAddEndpoint();
              }}
            />
          )}
        </div>
      </div>
      {instructionText && <p className="text-xs text-gray-500 mb-2 px-1">{instructionText}</p>}
      {nameError && (
        <p className="text-xs text-red-600 mb-2 px-1" style={{ marginBottom: 0 }}>* {nameError}</p>
      )}
      <div className="flex-1 min-h-0 overscroll-contain">
        <Grid
          key={selectedGroupId ?? 'none'}
          data={filteredEndpoints}
          style={{ height: '100%' }}
          dataItemKey={DATA_ITEM_KEY}
          onItemChange={handleItemChange}
          sortable={true}
          sort={sort}
          onSortChange={(event: GridSortChangeEvent) => setSort(event.sort)}
          editable={true}
          editField="inEdit"
        >
          <GridColumn field="name" title={localizationService.toLanguageString('custom.name', enMessages.custom.name)} />
          {onAddEndpoint && (
            <GridColumn
              title={localizationService.toLanguageString('custom.actions', enMessages.custom.actions)}
              width="100px"
              sortable={false}
              filterable={false}
              editable={false}
              cells={{ data: deleteCell }}
            />
          )}
        </Grid>
      </div>
    </div>
  );
};

export default EndpointsTableTemplateComponent;
